import React, { useEffect, useRef, useState } from 'react';
import { useCurrency, useI18n, useLicenses, useLogger, useMetadata, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import { useOrderStatusApi, useOrderStatusUniverse } from '~Components/OrderStatusUniverse';
import { LocalItem, TicketMessage } from '~Types';
import {
  getBongData_categories,
  getBongData_operativeViewer_ticketTemplates,
} from './__queries__';
import TicketController, { Printer, Station, Ticket } from '@baemingo/ticket-controller';
import styled from '@emotion/styled';
import { toPng } from 'html-to-image';
import TicketImageDesign from './TicketImageDesign';
import { useTicketBuilder, ProcessedTicketData } from './useTicketBuilder';
import { generateTicketPrintData } from './TicketTextDesign';
import { removeSpecialCharactersForEpson } from './Layout';
import { processKDSOrder } from './processKDSOrder';
import { ORDER_ORIGIN } from '@baemingo/order-status-events/dist/types';
import useCheckPrinterImageSupport from '~Hooks/useCheckPrinterImageSupport';
import useConfirm from '~Hooks/useConfirm';
import { useLocation } from 'react-router';
import { TicketOrderInfo } from '.';

const TicketWrapper = styled.div`
  position: fixed;
  pointer-events: none;
  opacity: 0;
  width: 300px;
`;

interface Props {
  groupId: string;
  items: LocalItem[];
  messages?: LocalItem[];
  categories: getBongData_categories[];
  onlyPhysicalBongs?: boolean;
  orderInfo: TicketOrderInfo;
  onCompleted: () => void;
  ticketMessages?: TicketMessage[];
  skipConfirmation?: boolean;
  ticketTemplates?: getBongData_operativeViewer_ticketTemplates[];
}

const ControlledFlow: React.FunctionComponent<Props> = ({
  groupId,
  items,
  messages,
  categories,
  orderInfo,
  onCompleted,
  onlyPhysicalBongs,
  ticketMessages,
  skipConfirmation,
  ticketTemplates,
}) => {
  const [, , venueId] = location.hash.split('/');
  const logger = useLogger('printer');
  const ticketRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [storage] = useStorage();
  const meta = useMetadata();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [processedTicketsData, setProcessedTicketsData] = useState<ProcessedTicketData[]>([]);
  const [kdsOrderId, setKdsOrderId] = useState<string | undefined>(undefined);
  const currency = useCurrency();
  const { i18n } = useI18n();
  const licenses = useLicenses();
  const orderStatusApi = useOrderStatusApi(venueId);
  const orderStatus = useOrderStatusUniverse();
  const { checkImagePrintingSupported } = useCheckPrinterImageSupport();
  const { confirm } = useConfirm();
  const currentRoute = useLocation();
  const stations = useSelector((state) => state.stations) || [];

  const courseTypes = useSelector((state) => state.courseTypes);
  const printers = useSelector((state) => state.printers);

  // Check if we are in EPOS (express or self-service-kiosk) where we should skip the confirmation
  const isEpos = ['express', 'self-service-kiosk'].some((path) =>
    currentRoute.pathname.includes(path),
  );

  // The ticket builder makes the general layout and content of the ticket, used for both image and text printing
  const ticketBuilder = useTicketBuilder({
    items,
    messages,
    categories,
    orderInfo,
    ticketTemplates,
  });

  useEffect(() => {
    logger.info('[CONTROLLED FLOW] Using new Controlled Flow. Title: ' + orderInfo.title);

    // processKDSOrder is async and dispatches several events with `await sleep(2)`
    // between them. The returned orderId is needed to link the printed bong to
    // the KDS order in the backend, so the rest of this effect must wait for it.
    (async () => {
      const { orderId } = await processKDSOrder({
        items,
        stations,
        orderInfo,
        printers,
        storage,
        atleastOneDeviceHasKDS: licenses.hasKDS || licenses.atleastOneDeviceHasKDS,
        useCoursesInKDS: meta.data.useCoursesInKDS,
        orderStatusApi,
        onlyPhysicalBongs,
        orderStatus,
        logger,
        ticketMessages,
      });

      // Store the orderId for use in printing
      setKdsOrderId(orderId);

      // If we are letting the KDS print bongs on progress, we don't want it to print the regular first bong.
      // To distinct between the two, we use the flag onlyPhysicalBongs, which is only true on the KDS progress bongs.
      const skipRegularBongIfKDSBongOnProgress = storage.KDSBongOnProgress && !onlyPhysicalBongs;
      if (skipRegularBongIfKDSBongOnProgress) {
        logger.info('[CONTROLLED FLOW] Not printing because we are skipping regular bongs', {
          skipRegularBongIfKDSBongOnProgress: skipRegularBongIfKDSBongOnProgress,
          kdsBongOnProgress: storage.KDSBongOnProgress,
          onlyPhysicalBongs: onlyPhysicalBongs,
        });
        onCompleted();
        return;
      }

      if (!storage.enableBongPrints) {
        logger.info('[CONTROLLED FLOW] Bong prints are disabled in storage settings');
        onCompleted();
        return;
      }

      const _printers: Printer[] = printers.map((printer) => ({
        id: printer.id,
        stationIds: printer.stations,
      }));

      if (_printers.some((printer) => !printer.id)) {
        logger.error(
          '[CONTROLLED FLOW] Found printer with missing ID, this printer will be ignored',
          {
            printers: _printers,
          },
        );
      }

      // Guard against faulty printer with missing IDs.
      const filteredPrinters = _printers.filter((printer) => printer.id);

      const _stations: Station[] = stations.map((station) => ({
        id: station.id,
        categoryIds: station.categories.map((c) => c.id),
        itemIds: station.items,
        origins: station.origins,
        showItemsFromOtherStations: station.showAllItems !== false,
        includeUnmatchedCourseGroups: !storage.useNewPrinterFlow, // if useNewPrinterFlow is false, we want to include unmatched course groups (like in the old flow) if true we only include course items from other station if they are in the same course group as an item from this station
      }));

      const courseTypesOrder = courseTypes.reduce((acc, ct) => {
        acc[ct.id] = ct.order;
        return acc;
      }, {} as { [courseTypeId: string]: number });

      const controller = new TicketController(filteredPrinters, _stations, {
        printOneTicketPerCourseType: storage.bongGroupByStation,
        printOneTicketPerStation: storage.bongPrintByStation,
        splitItemGroupsByCategory: storage.bongGroupByCategory,
        courseOrder: courseTypesOrder,
      });

      logger.info('[CONTROLLED FLOW] Ticket controller initialized', {
        printers: filteredPrinters,
        stations: _stations,
        settings: {
          printOneTicketPerCourseType: storage.bongGroupByStation,
          printOneTicketPerStation: storage.bongPrintByStation,
          splitItemGroupsByCategory: storage.bongGroupByCategory,
        },
        courseTypesOrder,
      });

      logger.info('[CONTROLLED FLOW] Sending order to ticket controller', {
        origin: orderInfo.origin,
        serviceType: orderInfo.serviceType,
        items,
        messages: ticketMessages,
      });

      // Creates the number of tickets to be printed and their items/groups. The ticket builder below takes care of the layout and design
      try {
        const controllerTickets = controller.send({
          origin: orderInfo.origin!,
          serviceType: orderInfo.serviceType!,
          items: items.map((item) => ({
            id: item.id,
            categoryId: item.categoryId,
            courseType: item.courseType?.id || null,
            itemId: item.itemId,
            title: item.title,
          })),
          messages:
            ticketMessages?.map((msg) => ({
              title: msg.message,
              courseType: msg.courseId || null,
            })) || [],
        });

        if (controllerTickets.tickets.length === 0) {
          logger.info('[CONTROLLED FLOW] No tickets to print, completing flow.');
          onCompleted();
          return;
        }

        logger.info(`${orderInfo.title} - ${controllerTickets.tickets.length} tickets`, {
          tickets: controllerTickets,
        });

        // Process ticket data for each ticket
        const processedData: ProcessedTicketData[] = [];

        for (const ticket of controllerTickets.tickets) {
          if (!ticket.itemGroups.length) {
            continue;
          }

          // Process the ticket data using the hook
          const ticketProcessedData = ticketBuilder.createTicket(ticket);

          logger.info('[CONTROLLED FLOW] Ticket processed', {
            ticket,
            ticketProcessedData,
          });

          processedData.push(ticketProcessedData);
        }

        if (processedData.length === 0) {
          logger.info('[CONTROLLED FLOW] No printable tickets, completing flow.');
          onCompleted();
          return;
        }

        setTickets(controllerTickets.tickets.filter((t) => t.itemGroups.length > 0));
        setProcessedTicketsData(processedData);
      } catch (error) {
        logger.error('[CONTROLLED FLOW] Error processing tickets', {
          errorData: error,
        });
        // If there is an error in processing the tickets, we should still complete the flow to avoid blocking the user
        onCompleted();
      }
    })();
  }, [storage.KDSBongOnProgress]);

  useEffect(() => {
    if (tickets.length === 0 || processedTicketsData.length === 0) return;

    const executePrint = async () => {
      // Show confirmation dialog if askToPrintBong is enabled
      // Only show this once for all tickets, not per ticket
      // Skip confirmation if skipConfirmation flag is set (e.g., from Spain ParkedOrderPrintsModal)
      if (storage.askToPrintBong && !isEpos && !skipConfirmation) {
        logger.info('[CONTROLLED FLOW] Asking user to confirm bong printing');
        const shouldPrint = await confirm(i18n('ParkOrder.BongPrompt'));

        if (!shouldPrint) {
          logger.info('[CONTROLLED FLOW] User cancelled bong printing');
          // Clear tickets and complete without printing
          setTickets([]);
          setProcessedTicketsData([]);
          onCompleted();
          return;
        }

        logger.info('[CONTROLLED FLOW] User confirmed bong printing, proceeding with print');
      }

      try {
        for (let i = 0; i < tickets.length; i++) {
          const ticketPrinter = printers.find((p) => p.id === tickets[i].printerId);

          const imagePrintingSupported = checkImagePrintingSupported(ticketPrinter?.model || '');

          // Only do image printing if setting is enabled and printer is star cloud
          if (imagePrintingSupported) {
            const ticketElement = ticketRefs.current[i];

            // Before sending ticketElement to png, the opacity needs to be set to 1, otherwise the image will be black
            if (ticketElement) {
              ticketElement.style.opacity = '1';
            }

            if (!ticketElement) {
              logger.error('[CONTROLLED FLOW] No ticket element found to print as image');
              continue;
            }

            const base64String = await toPng(ticketElement, {
              pixelRatio: 2, //2 becuase the react element is 300px wide and we want to double it to 600px (20px space on each side of the paper, paper is 640px wide)
              width: 300, // 300px is the width of the ticket
              skipFonts: true,
            });

            logger.info(
              `[CONTROLLED FLOW] Printing ticket ${i + 1} of ${tickets.length} as image`,
              {
                printerId: tickets[i].printerId,
                groupId: groupId,
              },
            );

            window.printerManager?.initiatePrint({
              printerId: tickets[i].printerId,
              printType: 'BONG',
              orderId: kdsOrderId, // Used to link the print to the KDS order in the backend, can be undefined
              base64Image: base64String,
            });
          } else {
            // Generate PrintRow[] data for text-based printing
            const printRowData = generateTicketPrintData(
              processedTicketsData[i],
              currency.format,
              storage,
              i18n,
              orderInfo.origin === ORDER_ORIGIN.APP,
            );

            // Remove unsupported characters for Epson printers (e.g. '➤' prints as '?')
            if (ticketPrinter) {
              removeSpecialCharactersForEpson({
                printContent: printRowData,
                printer: ticketPrinter,
              });
            }

            logger.info(
              `[CONTROLLED FLOW] Printing ticket ${i + 1} of ${tickets.length} as text`,
              {
                printerId: tickets[i].printerId,
                orderId: groupId,
              },
            );

            window.printerManager?.initiatePrint({
              printerId: tickets[i].printerId,
              printType: 'BONG',
              data: printRowData,
              orderId: kdsOrderId, // Used to link the print to the KDS order in the backend, can be undefined
            });
          }
        }

        // After printing, clear
        setTickets([]);
        setProcessedTicketsData([]);
        onCompleted();
      } catch (error) {
        logger.error('[CONTROLLED FLOW] Error in printing tickets', {
          errorData: error,
        });
      }
    };

    executePrint();
  }, [tickets, processedTicketsData]);

  if (!tickets.length || !processedTicketsData.length || storage.disableImagePrinting) return null;

  return (
    <TicketWrapper>
      {tickets?.map((_, index) => {
        return (
          <div key={index}>
            <div ref={(el) => (ticketRefs.current[index] = el)}>
              <TicketImageDesign
                ticketData={processedTicketsData[index]}
                formatCurrency={currency.format}
                isAppOrder={orderInfo.origin === ORDER_ORIGIN.APP}
              />
            </div>
          </div>
        );
      })}
    </TicketWrapper>
  );
};

export default ControlledFlow;
